import type { GoogleAdsConfig } from "@/config/env";
import { routePattern } from "../analytics";
import type { AdAdapter, AdEvent } from "./types";

/**
 * Google Ads, to count the sign-ups and purchases an ad brought: the
 * Google tag and one conversion for each account made, each plan started
 * and each pack bought. Nothing else is sent: no page views, and ad
 * personalisation is off, so Google can count a conversion but not build
 * an audience from it.
 *
 * When it may run is the relay's rule (lib/ads/relay.ts). Until then no
 * Google script loads; once it has, going off denies its storage and
 * sends nothing more.
 */

const LABELS: Record<AdEvent["kind"], (config: GoogleAdsConfig) => string | null> = {
    signup: (config) => config.signupLabel,
    plan: (config) => config.planLabel,
    pack: (config) => config.packLabel,
};

declare global {
    interface Window {
        dataLayer?: unknown[];
    }
}

export function googleAdapter(config: GoogleAdsConfig): AdAdapter {
    let started = false;
    let configured = false;

    /** Google's `gtag`: it reads each call's `arguments` object from the data layer, not an array. */
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    function gtag(..._args: unknown[]): void {
        if (!started) return;
        // eslint-disable-next-line prefer-rest-params
        window.dataLayer?.push(arguments);
    }

    return {
        name: "google",
        accepts: () => true,
        /** Loads the Google tag once, with every kind of storage denied until `grant`. */
        load() {
            if (started) return;
            started = true;
            window.dataLayer = window.dataLayer ?? [];
            gtag("consent", "default", {
                ad_storage: "denied",
                ad_user_data: "denied",
                ad_personalization: "denied",
                analytics_storage: "denied",
            });
            const script = document.createElement("script");
            script.async = true;
            script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(config.id)}`;
            document.head.appendChild(script);
        },
        grant() {
            gtag("consent", "update", { ad_storage: "granted", ad_user_data: "granted" });
            // Configured after the grant, so the tag keeps the ad's click id
            // from the address the first time it reads it.
            if (!configured) {
                configured = true;
                gtag("js", new Date());
                gtag("config", config.id, {
                    send_page_view: false,
                    allow_ad_personalization_signals: false,
                    // Google as a service provider under US state privacy law.
                    restricted_data_processing: true,
                });
            }
        },
        revoke() {
            gtag("consent", "update", { ad_storage: "denied", ad_user_data: "denied" });
        },
        send(event) {
            const label = LABELS[event.kind](config);
            if (!label) return;
            gtag("event", "conversion", {
                send_to: `${config.id}/${label}`,
                // The page as analytics keeps it: its route's pattern, no query.
                page_location: `${window.location.origin}${routePattern(window.location.pathname)}`,
                ...(event.value !== null && event.currency
                    ? { value: event.value, currency: event.currency }
                    : {}),
                ...(event.transactionId ? { transaction_id: event.transactionId } : {}),
            });
        },
    };
}
